import { Fragment, useCallback, useEffect, useReducer, useState } from 'react';

import api, {
  PresencaElement,
  PresencaElementTipoPresenca,
} from 'api/Assessor/presenca';
import ErrorHandler from 'helpers/utils/ErrorHandler';

import { Tooltip } from '@material-ui/core';

import MobileIcon from 'components/Icons/MobileIcon';
import NewAgencyIcon from 'components/Icons/NewAgencyIcon';
import { formatCPFOrCNPJ } from 'helpers/utils/cpfCnpjMask';
import { ReactComponent as BiometricIcon } from '../biometric.svg';
import { ReactComponent as FaceIcon } from '../face.svg';

import moment from 'moment';
import {
  AgencyIcon,
  Col,
  IconButton,
  NavigatorWithMouse,
  Paginator,
  Row,
  Select,
  Table,
} from 'plataforma-fundacao-componentes';
import { tabelaInitialState, tabelaReducer } from './reducer';
import useStyle from './style';

import Evidence from 'components/Modal/Evidence';
import { AssemblyModalitiesNumber } from 'helpers/enums/Assembleia';
import { getUniqueKey } from 'helpers/utils/KeyUtils';
import translation from 'locales/translation';
import { useDispatch } from 'react-redux';
import { TypeOfCollection } from 'services/editVotes';
import { closeModal, openModal } from 'store/modules/ModalActions';
import './styles.scss';

interface Props {
  idEvento: number;
  agencias?: Array<string>;
  nucleos?: Array<string>;
  cpfOrNome?: string;
  needReload?: boolean;
  onReload?: () => void;
  modalities?: AssemblyModalitiesNumber;
}

export default function Tabela({
  idEvento,
  agencias,
  nucleos,
  cpfOrNome,
  needReload,
  onReload,
  modalities,
}: Props) {
  const classes = useStyle();
  const [{ rows, page, size, count }, dispatch] = useReducer(
    tabelaReducer,
    tabelaInitialState
  );

  const [totalPages, setTotalPages] = useState<number>();
  const [loading, setLoading] = useState<boolean>(false);

  const dispatchAction = useDispatch();

  const getData = useCallback(async () => {
    setLoading(true);
    const { fail, data } = await api.list({
      idEvento,
      page,
      size,
      agencias,
      nucleos,
      cpfOrNome,
    });
    if (fail) {
      ErrorHandler(fail);
      setLoading(false);
    }
    if (data) {
      setLoading(false);
      dispatch({ type: 'setRow', value: data.elements });
      dispatch({ type: 'setCount', value: data.totalElements });
      setTotalPages(data.totalPages);
    }
  }, [idEvento, page, size, agencias, nucleos, cpfOrNome]);

  const getIconOfTipoPresenca = (tipoPresenca: PresencaElementTipoPresenca) => {
    switch (tipoPresenca) {
      case PresencaElementTipoPresenca.ONLINE:
        return <NavigatorWithMouse />;
      case PresencaElementTipoPresenca.OFFLINE:
        return <MobileIcon />;
      case PresencaElementTipoPresenca.AGENCIA:
        return <NewAgencyIcon />;
      case PresencaElementTipoPresenca.PRESENCIAL:
        return <AgencyIcon />;
      case PresencaElementTipoPresenca.BIOMETRIA:
        return <MobileIcon />;
    }
  };

  const getTipoPresenca = (row: PresencaElement) => {
    switch (row.tipoPresenca) {
      case PresencaElementTipoPresenca.ONLINE:
        return (
          <div className={classes.rowPresencaTipo}>
            {getIconOfTipoPresenca(row.tipoPresenca)}
            {translation.RelatorioPresentes.tipoPresenca[`${row.tipoPresenca}`]}
          </div>
        );
      case PresencaElementTipoPresenca.OFFLINE:
        return (
          <Tooltip
            title={`${
              translation.RelatorioPresentes.tipoAssinatura[
                `${row.tipoAssinatura}`
              ]
            } ${row.ldapPresencaAgencia ? `: ${row.ldapPresencaAgencia}` : ''}`}
            placement={'top'}
            arrow
          >
            <div className={classes.rowPresencaTipo}>
              {getIconOfTipoPresenca(row.tipoPresenca)}
              {
                translation.RelatorioPresentes.tipoPresenca[
                  `${row.tipoPresenca}`
                ]
              }
            </div>
          </Tooltip>
        );
      case PresencaElementTipoPresenca.AGENCIA:
        return (
          <Tooltip
            title={`${
              translation.RelatorioPresentes.tipoAssinatura[
                `${row.tipoAssinatura}`
              ]
            }: ${row.ldapPresencaAgencia}`}
            placement={'top'}
            arrow
          >
            <div className={classes.rowPresencaTipo}>
              {getIconOfTipoPresenca(row.tipoPresenca)}
              {
                translation.RelatorioPresentes.tipoPresenca[
                  `${row.tipoPresenca}`
                ]
              }
            </div>
          </Tooltip>
        );
      case PresencaElementTipoPresenca.PRESENCIAL:
        return (
          <Tooltip
            title={`${
              translation.RelatorioPresentes.tipoAssinatura[
                `${row.tipoAssinatura}`
              ]
            }: ${row.ldapPresencaAgencia}`}
            placement={'top'}
            arrow
          >
            <div className={classes.rowPresencaTipo}>
              {getIconOfTipoPresenca(row.tipoPresenca)}
              {
                translation.RelatorioPresentes.tipoPresenca[
                  `${row.tipoPresenca}`
                ]
              }
            </div>
          </Tooltip>
        );
      case PresencaElementTipoPresenca.BIOMETRIA:
        return (
          <Tooltip
            title={`${
              translation.RelatorioPresentes.tipoAssinatura[
                `${row.tipoAssinatura}`
              ]
            }: ${row.ldapPresencaAgencia}`}
            placement={'top'}
            arrow
          >
            <div className={classes.rowPresencaTipo}>
              {getIconOfTipoPresenca(row.tipoPresenca)}
              {
                translation.RelatorioPresentes.tipoPresenca[
                  `${row.tipoPresenca}`
                ]
              }
            </div>
          </Tooltip>
        );
    }
  };

  const handleOpenEvidence = useCallback(
    (idConvocado: number, idEvento: number) => {
      const modalKey: string = getUniqueKey();

      return dispatchAction(
        openModal(Evidence, {
          idConvocado,
          idEvento,
          modalKey: modalKey,
          preventEscExit: true,
          preventMaskExit: false,
          mobileOnXS: false,
          onClose: () => closeModal(modalKey),
        })
      );
    },
    [dispatchAction]
  );

  useEffect(() => {
    getData();
  }, [getData]);

  useEffect(() => {
    if (!needReload) return;
    getData();
    onReload && onReload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needReload, onReload]);

  useEffect(() => {
    dispatch({
      type: 'setPage',
      value: 1,
    });
  }, [count]);

  return (
    <Row className={'root'}>
      <Col>
        <Table
          columns={[
            {
              key: 'nomeConvocado',
              value: 'Nome do convocado',
              props: {
                align: 'left',
              },
            },
            {
              key: 'cpfcnpj',
              value: 'CPF/CNPJ',
              props: {
                align: 'center',
              },
            },
            {
              key: 'agencia',
              value: 'Agência',
              props: {
                align: 'center',
              },
            },
            {
              key: 'nucleo',
              value: 'Núcleo',
              props: {
                align: 'center',
              },
            },
            {
              key: 'conta',
              value: 'Conta',
              props: {
                align: 'center',
              },
            },
            {
              key: 'coordenador',
              value: 'Coordenador',
              props: {
                align: 'center',
              },
            },
            {
              key: 'coordenadorOuConselheiro',
              value: 'Colaborador ou conselheiro',
              props: {
                align: 'center',
              },
            },
            {
              key: 'presencaConfirmada',
              value: 'Presença confirmada',
              props: {
                align: 'center',
              },
            },
            {
              key: 'tipoPresenca',
              value: 'Tipo de presença',
              props: {
                align: 'center',
              },
            },
            {
              key: 'dataHoraPresenca',
              value: 'Data e hora presença',
              props: {
                align: 'center',
              },
            },
            {
              key: 'evidencia',
              value: 'Evidência',
              props: {
                align: 'center',
              },
            },
          ]}
          lines={rows.map((row: any) => {
            return {
              nomeConvocado: <div>{row.nomeConvocado}</div>,
              cpfcnpj: formatCPFOrCNPJ(row.cpfCnpj),
              agencia: row.agencia,
              nucleo: row.nucleo,
              conta: row.conta,
              coordenador: row.coordenador,
              coordenadorOuConselheiro: row.colaborador,
              presencaConfirmada: <div>{row.presencaConfirmadaPor}</div>,
              tipoPresenca: <div>{getTipoPresenca(row)}</div>,
              dataHoraPresenca: (
                <div>
                  {moment(row.dataHoraPresenca).format('DD/MM/YYYY - HH:mm')}
                </div>
              ),
              evidencia: [
                AssemblyModalitiesNumber.HIBRIDO_DIGITAL_AGENCIA,
                AssemblyModalitiesNumber.HIBRIDO_PRESENCIAL_DIGITAL,
                AssemblyModalitiesNumber.HIBRIDO_SIMULTANEO,
              ].includes(modalities!) ? (
                [TypeOfCollection.BIOMETRIC, TypeOfCollection.FACE].includes(
                  row.tipoAssinatura
                ) ? (
                  <IconButton
                    tooltip-text={'Visualizar'}
                    icon={
                      row.tipoAssinatura === TypeOfCollection.BIOMETRIC ? (
                        <BiometricIcon />
                      ) : row.tipoAssinatura === TypeOfCollection.FACE ? (
                        <FaceIcon />
                      ) : (
                        <Fragment />
                      )
                    }
                    onClick={() =>
                      handleOpenEvidence(row.oidConvocado, idEvento)
                    }
                  />
                ) : (
                  '-'
                )
              ) : (
                '-'
              ),
            };
          })}
        />
      </Col>
      <Col cols={[12, 12, 6, 6, 6]}>
        <Col cols={[12, 12, 6, 6, 4]} noPadding>
          <Select
            data-testid={'row-per-page'}
            label={'Linhas por página'}
            options={[
              {
                label: '10',
                value: 10,
              },
              {
                label: '25',
                value: 25,
              },
              {
                label: '50',
                value: 50,
              },
              {
                label: '100',
                value: 100,
              },
            ]}
            onChange={(e) => {
              dispatch({
                type: 'setSize',
                value: e,
              });
              dispatch({
                type: 'setPage',
                value: 1,
              });
            }}
            placeholder={'teste'}
            value={size}
            disabled={false}
            showPanelFooter={false}
            clearButton={false}
          />
        </Col>
      </Col>
      <Col cols={[12, 12, 6, 6, 6]} end>
        {totalPages ? (
          <Paginator
            paginaAtual={page}
            quantidadeDePaginas={totalPages!}
            maxLength={7}
            loading={loading || needReload}
            onChange={(e: number) =>
              dispatch({
                type: 'setPage',
                value: e,
              })
            }
          />
        ) : undefined}
      </Col>
    </Row>
  );
}
